/**
 * Тёмная и светлая темы (ТЗ §5.1).
 *
 * Три состояния, а не два. «Как в системе» — это не третья тема, а отказ выбирать:
 * пользователь, у которого macOS переключается по расписанию, хочет, чтобы приложение
 * следовало за ней, и запомненное «светлая» ночью его удивит.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'crm.theme'

/** Сохранённый выбор; по умолчанию — следовать за системой. */
export function themePreference(): ThemePreference {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
}

/** Во что «как в системе» разворачивается прямо сейчас. */
export function resolveTheme(preference: ThemePreference = themePreference()): ResolvedTheme {
  if (preference !== 'system') return preference
  // matchMedia отсутствует в jsdom без явной заглушки — отсюда проверка, а не
  // прямой вызов: тест не должен падать из-за отсутствия браузерного API.
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  return prefersDark ? 'dark' : 'light'
}

/**
 * Применяет тему к документу.
 *
 * Атрибут на `<html>`, а не класс на `<body>`: CSS-переменные объявляются на корне, и
 * тема должна действовать до того, как отрисуется первый элемент. Иначе при загрузке
 * мелькает светлый фон — то самое, из-за чего тёмную тему и включают.
 */
export function applyTheme(preference: ThemePreference): void {
  localStorage.setItem(STORAGE_KEY, preference)
  document.documentElement.dataset.theme = resolveTheme(preference)
}

/**
 * Подписка на смену системной темы.
 *
 * Нужна только когда выбрано «как в системе»: при явном выборе системные изменения
 * игнорируются, иначе выбор пользователя перезаписывался бы операционной системой.
 */
export function watchSystemTheme(onChange: (theme: ResolvedTheme) => void): () => void {
  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  if (!media) return () => {}

  const listener = (event: MediaQueryListEvent) => {
    if (themePreference() === 'system') {
      onChange(event.matches ? 'dark' : 'light')
    }
  }
  media.addEventListener('change', listener)
  return () => media.removeEventListener('change', listener)
}
